import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Stable client id (localStorage)
export function getClientId() {
  let cid = localStorage.getItem("handy_client_id");
  if (!cid) {
    cid = (crypto && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("handy_client_id", cid);
  }
  return cid;
}

export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((config) => {
  config.headers["X-Client-Id"] = getClientId();
  return config;
});

export async function analyzePrescriptionImage(base64, mime) {
  const r = await api.post("/prescriptions/analyze", { image_base64: base64, mime_type: mime || "image/jpeg" });
  return r.data;
}

export const rx = {
  list: () => api.get("/prescriptions").then(r => r.data),
  get: (id) => api.get(`/prescriptions/${id}`).then(r => r.data),
  create: (payload) => api.post("/prescriptions", payload).then(r => r.data),
  update: (id, patch) => api.patch(`/prescriptions/${id}`, patch).then(r => r.data),
  detectTapering: (id) => api.post(`/prescriptions/${id}/detect-tapering`).then(r => r.data),
  remove: (id) => api.delete(`/prescriptions/${id}`).then(r => r.data),
  adherence: (id) => api.get(`/prescriptions/${id}/adherence`).then(r => r.data),
  share: (id) => api.post(`/prescriptions/${id}/share`).then(r => r.data),
  revokeShare: (id) => api.delete(`/prescriptions/${id}/share`).then(r => r.data),
  icsUrl: (id) => `${API}/prescriptions/${id}/ics`,
  pdfUrl: (id) => `${API}/prescriptions/${id}/pdf`,
  activityCsvUrl: (id) => `${API}/prescriptions/${id}/activity.csv`,
};

export const publicShare = {
  get: (token) => axios.get(`${API}/share/${token}`).then(r => r.data),
  react: (token, doseId, emoji, fromName) =>
    axios.post(`${API}/share/${token}/react`, { dose_id: doseId, emoji, from_name: fromName }).then(r => r.data),
  subscribe: (token, endpoint, keys, caregiverName) =>
    axios.post(`${API}/share/${token}/subscribe`, { endpoint, keys, caregiver_name: caregiverName }).then(r => r.data),
  unsubscribe: (token, endpoint, keys) =>
    axios.post(`${API}/share/${token}/unsubscribe`, { endpoint, keys }).then(r => r.data),
  subscriptionStatus: (token, endpoint) =>
    axios.get(`${API}/share/${token}/subscription`, { params: { endpoint } }).then(r => r.data),
  digestSubscribe: (token, email, caregiverName) =>
    axios.post(`${API}/share/${token}/digest/subscribe`, { email, caregiver_name: caregiverName }).then(r => r.data),
  digestUnsubscribe: (token, email) =>
    axios.post(`${API}/share/${token}/digest/unsubscribe`, { email }).then(r => r.data),
  digestSubscription: (token, email) =>
    axios.get(`${API}/share/${token}/digest/subscription`, { params: { email } }).then(r => r.data),
};

export const digest = {
  status: () => axios.get(`${API}/digest/status`).then(r => r.data),
  stats: (rxId) => axios.get(`${API}/prescriptions/${rxId}/digest-stats`, { headers: { "X-Client-Id": getClientId() } }).then(r => r.data),
};

export const reactions = {
  list: (rxId) => api.get(`/prescriptions/${rxId}/reactions`).then(r => r.data),
};

export const caregivers = {
  list: (rxId) => api.get(`/prescriptions/${rxId}/caregivers`).then(r => r.data),
  remove: (rxId, subId) => api.delete(`/prescriptions/${rxId}/caregivers/${subId}`).then(r => r.data),
};

export const adherenceSummary = {
  send: () => api.post("/push/adherence-summary").then(r => r.data),
};

export const doses = {
  update: (id, status) => api.patch(`/doses/${id}`, { status }).then(r => r.data),
  bulkTakeToday: (rxId, dateStart, dateEnd) =>
    api.post(`/prescriptions/${rxId}/doses/bulk-take-today`,
             { date_start: dateStart, date_end: dateEnd }).then(r => r.data),
};

export const todos = {
  list: () => api.get("/todos").then(r => r.data),
  create: (p) => api.post("/todos", p).then(r => r.data),
  update: (id, p) => api.patch(`/todos/${id}`, p).then(r => r.data),
  remove: (id) => api.delete(`/todos/${id}`).then(r => r.data),
};

export const shopping = {
  list: () => api.get("/shopping").then(r => r.data),
  create: (p) => api.post("/shopping", p).then(r => r.data),
  update: (id, p) => api.patch(`/shopping/${id}`, p).then(r => r.data),
  remove: (id) => api.delete(`/shopping/${id}`).then(r => r.data),
};

export const notes = {
  list: () => api.get("/notes").then(r => r.data),
  create: (p) => api.post("/notes", p).then(r => r.data),
  update: (id, p) => api.patch(`/notes/${id}`, p).then(r => r.data),
  remove: (id) => api.delete(`/notes/${id}`).then(r => r.data),
};

export const google = {
  status: () => api.get("/google/status").then(r => r.data),
  authUrl: () => api.get("/google/auth-url").then(r => r.data),
  disconnect: () => api.post("/google/disconnect").then(r => r.data),
  pushPrescription: (rxId, includePast = false) =>
    api.post(`/google/push-prescription/${rxId}`, { include_past: includePast, dose_minutes: 15 }).then(r => r.data),
};

export const privacy = {
  forgetDevice: () => api.delete("/client-data").then(r => r.data),
};

export const fx = {
  rates: () => api.get("/fx/rates").then(r => r.data),
};

// Download authenticated binary via fetch + client id header
export async function downloadFile(url, filename) {
  const res = await fetch(url, { headers: { "X-Client-Id": getClientId() } });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
